<template>
    <template v-if="user">
        <div class="d-flex flex-column" style="height: 100%; min-height: 0">
            <!-- Two siblings: content + footer -->

            <!-- Content -->
            <v-container
                class="flex-grow-1 d-flex flex-column"
                style="min-height: 0"
            >
                <!-- Fixed header -->
                <v-row class="flex-shrink-0 flex-grow-0">
                    <v-col style="align-self: flex-start">
                        <DayNavigator
                            v-model="entriesDate"
                            @update:modelValue="entriesDate = $event"
                            range
                        />
                    </v-col>
                </v-row>

                <!-- Scrollable table section -->
                <v-row class="flex-grow-1" style="min-height: 0">
                    <v-col
                        class="d-flex flex-column flex-grow-1"
                        style="height: 100%; min-height: 0"
                    >
                        <ProjectsTable
                            v-if="!showEditTimeEntries"
                            :tableData="extendedReportData"
                            :totalTimeWorked="totalTimeWorked"
                            :totalOvertimeWorked="totalOvertimeWorked"
                            :currentTab="'today'"
                            :enableReordering="false"
                            :enablePriority="false"
                            :enableClickNavigation="false"
                            :showOdooExport="hasEntriesForExport"
                            @export-odoo="exportForOdoo"
                        />
                        <EditableTimeEntries
                            v-if="showEditTimeEntries"
                            ref="editEntries"
                            :timeEntries="currentTimeEntries"
                            :totalTimeWorked="totalTimeWorked"
                            :projects="projects"
                            :selectedDate="entriesDate"
                            :user="user"
                        />
                    </v-col>
                </v-row>
            </v-container>

            <!-- Footer -->
            <BottomButtonWrapper :isShowing="true">
                <!-- Not editing: shows BACK + EDIT HOURS -->
                <template v-if="!showEditTimeEntries && canEditWorkHours">
                    <v-col cols="6">
                        <ThemedActionButton
                            :buttonColor="'success'"
                            :text="'BACK'"
                            @click="goBack"
                        />
                    </v-col>
                    <v-col cols="6">
                        <ThemedActionButton
                            :buttonColor="'error'"
                            :text="'EDIT HOURS'"
                            @click="showEditableTimeEntries"
                        />
                    </v-col>
                </template>

                <!-- Editing: shows the BACK and ADD ENTRY -->
                <template v-else-if="showEditTimeEntries">
                    <v-col cols="6">
                        <ThemedActionButton
                            :buttonColor="'success'"
                            :text="'BACK'"
                            @click="showEditTimeEntries = false"
                        />
                    </v-col>
                    <v-col v-if="currentTimeEntries.length === 0" cols="6">
                        <ThemedActionButton
                            :buttonColor="'primary'"
                            :text="'ADD ENTRY'"
                            @click="$refs.editEntries?.addFirstEntry()"
                        />
                    </v-col>
                </template>
            </BottomButtonWrapper>
        </div>
    </template>
</template>

<script>
import { mapGetters } from "vuex";
import {
    calculateProjectsWorkSummaryForUser,
    calculateTotalTimeWorked,
    groupDescriptionsByProject,
    extendSummaryWithDescriptions,
    filterTimeEntriesForDate,
} from "@/utils/utils";
import ProjectsTable from "@/components/ProjectsTable.vue";
import EditableTimeEntries from "@/components/EditableTimeEntries.vue";
import BottomButtonWrapper from "@/components/BottomButtonWrapper.vue";
import ThemedActionButton from "@/components/ThemedActionButton.vue";
import DayNavigator from "@/components/DayNavigator.vue";
import {
    buildOdooExportRows,
    exportOdooCsv,
} from "@/utils/exportUtils";
import dayjs from "dayjs";

export default {
    components: {
        ProjectsTable,
        EditableTimeEntries,
        BottomButtonWrapper,
        ThemedActionButton,
        DayNavigator,
    },
    data() {
        const today = dayjs().startOf("day").toDate();
        return {
            entriesDate: [today, today],
            showEditTimeEntries: false,
        };
    },
    methods: {
        showEditableTimeEntries() {
            this.showEditTimeEntries = true;
        },
        goBack() {
            this.$router.back();
        },
        odooExportFilename() {
            if (!this.entriesDate?.length) {
                return "odoo-timesheet.csv";
            }
            const start = dayjs(this.entriesDate[0]).format("YYYY-MM-DD");
            const end = dayjs(
                this.entriesDate[this.entriesDate.length - 1]
            ).format("YYYY-MM-DD");
            if (start === end) {
                return `odoo-timesheet-${start}.csv`;
            }
            return `odoo-timesheet-${start}_${end}.csv`;
        },
        exportForOdoo() {
            // Use all projects so shared-odooId umbrellas are detected even when
            // the other Pulse project is not on this user's working list.
            const rows = buildOdooExportRows(
                this.currentTimeEntries,
                this.projectsForOdooExport
            );
            exportOdooCsv(rows, { filename: this.odooExportFilename() });
        },
    },
    computed: {
        ...mapGetters({
            user: "getUser",
            projects: "getWorkingProjects",
            allProjects: "getAllProjects",
            getRights: "getRights",
        }),
        currentTimeEntries() {
            return filterTimeEntriesForDate(
                this.entriesDate,
                this.user.timeEntries
            );
        },
        projectsWithoutLunchBreak() {
            return this.projects.filter(
                (project) => project.projectId !== "SYSTEM_LUNCH-BREAK"
            );
        },
        projectsForOdooExport() {
            const source =
                Array.isArray(this.allProjects) && this.allProjects.length
                    ? this.allProjects
                    : this.projects || [];
            return source.filter(
                (project) => project.projectId !== "SYSTEM_LUNCH-BREAK"
            );
        },
        selectedDate() {
            return this.entriesDate;
        },
        dailyReportData() {
            if (!this.entriesDate || this.entriesDate.length === 0) return [];

            return calculateProjectsWorkSummaryForUser(
                this.user,
                this.projectsWithoutLunchBreak,
                {
                    dateRange: {
                        startDate: this.entriesDate[0],
                        endDate: this.entriesDate[this.entriesDate.length - 1],
                    },
                    excludeEmpty: true,
                }
            );
        },

        /**
         * Overall overtime total for the selected date/range,
         * reusing the existing calculateTotalTimeWorked by mapping
         * each project’s overtime string into the expected shape.
         */
        totalOvertimeWorked() {
            // build a list like [{ projectName, totalTimeWorked: "Xh:Ymin" }]
            const overtimeRows = this.dailyReportData.map((p) => ({
                projectName: p.projectName,
                totalTimeWorked: p.overtimeTimeWorked || "0h:0min",
            }));
            return calculateTotalTimeWorked(overtimeRows);
        },

        /**
         * Take the raw time entries + the per-project summary,
         * and produce a new array where each project object
         * also has a `descriptions: [ "Desc (30min)", … ]` array.
         */
        extendedReportData() {
            const descMap = groupDescriptionsByProject(this.currentTimeEntries);
            return extendSummaryWithDescriptions(this.dailyReportData, descMap);
        },
        totalTimeWorked() {
            return calculateTotalTimeWorked(this.dailyReportData);
        },
        canEditWorkHours() {
            return this.getRights?.editWorkHours || false;
        },
        hasEntriesForExport() {
            return this.dailyReportData.length > 0;
        },
    },
};
</script>
